import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Route as RouteIcon,
  Assignment as AssignmentIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import useResponsive from '../hooks/useResponsive';
import axios from 'axios';
import { BACKEND_BASE_URL } from '../config';
import FieldOfficerCorridorDashboard from './FieldOfficerCorridorDashboard';

function FieldOfficerDashboard() {
  const [assignedCorridors, setAssignedCorridors] = useState([]);
  const [selectedCorridor, setSelectedCorridor] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const {
    isMobile,
    isSmallMobile,
    mobileDrawerOpen,
    toggleMobileDrawer,
    closeMobileDrawer,
    getDrawerWidth,
    getAppBarHeight,
    getContainerPadding
  } = useResponsive();

  useEffect(() => {
    if (!user || user.type !== 'field_officer') {
      navigate('/login');
      return;
    }
    fetchAssignedCorridors();
  }, [user, navigate]);

  const fetchAssignedCorridors = async () => {
    const corridors = [];
    try {
      setLoading(true);
      setError('');
      
      if (!user.assigned_corridors || user.assigned_corridors.length === 0) {
        setAssignedCorridors([]);
        return;
      }

      for (const corridor of user.assigned_corridors) {
        try {
          // Handle corridor IDs with "/" symbol
          const encodedCorridorId = encodeURIComponent(corridor.corridor_id);
          const response = await axios.get(`${BACKEND_BASE_URL}/api/client/corridors/${encodedCorridorId}?project_id=${corridor.project_id}`);
          if (response.data) {
            corridors.push({
              ...response.data.corridor,
              project: response.data.project,
              project_id: corridor.project_id // Add project_id to corridor object
            });
          }
        } catch (corridorError) {
          console.error(`Error fetching corridor ${corridor.corridor_id}:`, corridorError);
          // Continue with other corridors even if one fails
        }
      }
      
      setAssignedCorridors(corridors);
    } catch (error) {
      console.error('Error fetching corridors:', error);
      setError(error.response?.data?.error || 'Failed to fetch assigned corridors');
    } finally {
      setLoading(false);
      // Automatically select the first corridor if available
      if (corridors.length > 0) {
        await handleCorridorSelect(corridors[0]);
      }
    }
  };

// No need for separate project fetch since we already have all project data in the corridor response
  const fetchProjectForCorridor = () => {
    // Project data is already available in the corridor response
    return Promise.resolve();
  };

  const handleCorridorSelect = (corridor) => {
    setSelectedCorridor(corridor);
    setSelectedProject(corridor.project);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#F79233',
          height: getAppBarHeight()
        }}
      >
        <Toolbar sx={{ minHeight: getAppBarHeight() }}>
          {/* Hamburger menu for mobile */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleMobileDrawer}
              sx={{ mr: 2 }}
              className="hamburger-menu"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant={isSmallMobile ? "subtitle1" : "h6"}
            component="div"
            sx={{
              flexGrow: 1,
              fontSize: isSmallMobile ? '1rem' : '1.25rem'
            }}
            className="responsive-text"
          >
            Field Officer Dashboard
          </Typography>

          <Typography
            variant={isSmallMobile ? "body2" : "subtitle1"}
            sx={{
              mr: isSmallMobile ? 1 : 2,
              fontSize: isSmallMobile ? '0.875rem' : '1rem'
            }}
            className="responsive-text"
          >
            {user.name}
          </Typography>

          <IconButton
            color="inherit"
            onClick={handleLogout}
            size={isSmallMobile ? "small" : "medium"}
          >
            <LogoutIcon fontSize={isSmallMobile ? "small" : "medium"} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: 300,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 300,
              boxSizing: 'border-box',
              marginTop: `${getAppBarHeight()}px`,
              height: `calc(100vh - ${getAppBarHeight()}px)`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            },
          }}
        >
          <Box sx={{
            overflow: 'auto',
            flex: 1,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            },
          }}>
            <List sx={{ padding: 0 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress sx={{ color: '#F79233' }} />
                </Box>
              ) : assignedCorridors.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No corridors assigned"
                    primaryTypographyProps={{
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }}
                  />
                </ListItem>
              ) : (
                assignedCorridors.map((corridor) => (
                  <ListItemButton
                    key={`${corridor.project_id}-${corridor.corridor_id}`}
                    selected={selectedCorridor?.corridor_id === corridor.corridor_id && selectedCorridor?.project_id === corridor.project_id}
                    onClick={() => handleCorridorSelect(corridor)}
                    sx={{
                      minHeight: 48,
                      px: 2,
                      py: 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <RouteIcon sx={{ color: '#F79233' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={corridor.corridor_id}
                      secondary={corridor.project?.po_number || `Project: ${corridor.project_id}`}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem'
                      }}
                      className="responsive-text"
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          </Box>
        </Drawer>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={closeMobileDrawer}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: getDrawerWidth(),
              boxSizing: 'border-box',
              marginTop: `${getAppBarHeight()}px`,
              height: `calc(100vh - ${getAppBarHeight()}px)`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            },
          }}
        >
          <Box sx={{
            overflow: 'auto',
            flex: 1,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            },
          }}>
            <List sx={{ padding: 0 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress sx={{ color: '#F79233' }} />
                </Box>
              ) : assignedCorridors.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No corridors assigned"
                    primaryTypographyProps={{
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }}
                  />
                </ListItem>
              ) : (
                assignedCorridors.map((corridor) => (
                  <ListItemButton
                    key={`${corridor.project_id}-${corridor.corridor_id}`}
                    selected={selectedCorridor?.corridor_id === corridor.corridor_id && selectedCorridor?.project_id === corridor.project_id}
                    onClick={() => {
                      handleCorridorSelect(corridor);
                      if (isSmallMobile) closeMobileDrawer();
                    }}
                    sx={{
                      minHeight: 48,
                      px: 2,
                      py: 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <RouteIcon sx={{ color: '#F79233' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={corridor.corridor_id}
                      secondary={corridor.project?.po_number || `Project: ${corridor.project_id}`}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem'
                      }}
                      className="responsive-text"
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          </Box>
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: isMobile ? '100%' : 'calc(100% - 300px)',
          marginTop: `${getAppBarHeight()}px`,
          backgroundColor: '#f5f5f5',
          minHeight: `calc(100vh - ${getAppBarHeight()}px)`,
          overflow: 'auto'
        }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              mx: getContainerPadding(),
              mt: getContainerPadding(),
              fontSize: isSmallMobile ? '0.875rem' : '1rem'
            }}
          >
            {error}
          </Alert>
        )}

        {selectedCorridor ? (
          <FieldOfficerCorridorDashboard
            corridor={selectedCorridor}
            project={selectedProject}
          />
        ) : (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            flexDirection: 'column',
            color: '#666',
            px: getContainerPadding(),
            textAlign: 'center'
          }}>
            <AssignmentIcon sx={{
              fontSize: isSmallMobile ? 40 : 60,
              mb: 2,
              color: '#F79233'
            }} />
            <Typography
              variant={isSmallMobile ? "h6" : "h5"}
              className="responsive-text"
            >
              Select a corridor to view details
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default FieldOfficerDashboard; 